import { useQuery } from '@tanstack/react-query'
import { round } from 'lodash'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { PaginationParams } from '@/types/params'
import { requiredString, studentClass } from '@/utils/zod'

import type { Group } from '../types'

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    course: z.object({
      id: requiredString(),
      display_name: requiredString()
    }),
    teacher: z.object({
      id: requiredString(),
      name: requiredString()
    }),
    co_teacher_1: z
      .object({
        id: requiredString(),
        name: requiredString()
      })
      .nullable(),
    lesson_count: z.number(),
    lesson_covered_count: z.number(),
    lesson_cancelled_count: z.number(),
    students_count: z.number(),
    student_classes: z.array(studentClass),
    hours_per_day_count: z.number(),
    hours_per_week_count: z.number(),
    hours_per_month_count: z.number(),
    hours_per_semester_count: z.number(),
    hours_per_year_count: z.number()
  })
)

const parseResponse = (response: z.infer<typeof responseSchema>): Group[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    course: {
      id: item.course.id,
      displayName: item.course.display_name
    },
    teacher: {
      id: item.teacher.id,
      name: item.teacher.name
    },
    coTeacher: item.co_teacher_1
      ? {
          id: item.co_teacher_1.id,
          name: item.co_teacher_1.name
        }
      : undefined,
    studentClasses: item.student_classes.map(studentClassItem => ({
      id: studentClassItem.id,
      name: studentClassItem.display_name
    })),
    lessonCount:
      item.lesson_count +
      item.lesson_cancelled_count +
      item.lesson_covered_count,
    studentsCount: item.students_count,
    regularLessonCount: item.lesson_count,
    coveredLessonCount: item.lesson_covered_count,
    cancelledLessonCount: item.lesson_cancelled_count,
    hoursDayCount: round(item.hours_per_day_count, 1),
    hoursWeekCount: round(item.hours_per_week_count, 1),
    hoursMonthCount: round(item.hours_per_month_count, 1),
    hoursSemesterCount: item.hours_per_semester_count,
    hoursYearCount: item.hours_per_year_count
  }))

export type CoursesGroupsParams = {
  ids?: string[]
  studentsId?: string[]
  classesId?: string[]
  coursesId?: string[]
  semester?: string
  teacherOrCoTeacherId?: string[]
  tutorId?: string
} & PaginationParams

const useCoursesGroups = (params: CoursesGroupsParams) =>
  useQuery({
    queryKey: ['coursesGroups', params],
    staleTime: 60 * 100,
    queryFn: async () => {
      const response = await httpService.panel.panelLessonsGroupsList({
        id: params.ids,
        student_id: params.studentsId,
        class_id: params.classesId,
        course_id: params.coursesId,
        semester_id: params.semester,
        teacher_or_co_teacher_id: params.teacherOrCoTeacherId,
        tutor_id: params.tutorId,
        ordering: 'course__name,name',
        page: params.page,
        // @ts-expect-error Waits for backend fix
        page_size: params.pageSize,
        fetchKeys: {
          id: true,
          name: true,
          course: true,
          teacher: true,
          co_teacher_1: true,
          student_classes: true,
          students_count: true,
          lesson_count: true,
          hours_per_day_count: true,
          hours_per_month_count: true,
          hours_per_semester_count: true,
          hours_per_week_count: true,
          hours_per_year_count: true,
          lesson_covered_count: true,
          lesson_cancelled_count: true
        }
      })
      const parsed = parseResponse(responseSchema.parse(response.results))

      return {
        list: parsed,
        count: response.count
      }
    },
    enabled: !!params.semester
  })

export default useCoursesGroups
